/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation } from '@apollo/client/react';
import { Box, Spinner } from '@utilitywarehouse/hearth-react';
import React, { useEffect, useState } from 'react';

import { CURRENCY_CODE } from '@constants/app';
import { SPREEDLY_ENVIRONMENT_KEY } from '@constants/config';
import { formatCurrency } from '@utils/format';

import { DepositErrorComponent } from './Error';
import { SPREEDLY_CHALLENGE_STATES } from './Spreedly/types';
import {
  challenge_container_class,
  challenge_frame_id,
  hidden_frame_id,
} from './constants';
import { completeDeposit3DS, continueDeposit3DS } from './query';
import { useTrackedOrderSubmit } from './useTrackedOrderSubmit';

interface ProcessPaymentProps {
  basketID: string;
  amount: string;
  transactionData: any;
  externalTransactionToken: string;
}

interface ThreeDSEvent {
  action: string;
  finalize: (transactionData: string) => void;
  context: {
    state: string;
    step: string;
  };
}

// Component used to handle Spreedly3DS challenge states
const ProcessPayment = ({
  basketID,
  amount,
  transactionData,
  externalTransactionToken,
}: ProcessPaymentProps): React.ReactElement => {
  const [challengeState, setChallengeState] =
    useState<SPREEDLY_CHALLENGE_STATES>(SPREEDLY_CHALLENGE_STATES.LOADING);

  const [challengeStarted, setChallengeStarted] = useState<boolean>(false);

  const { submitAndCompleteOrder, submitOrderError, submitOrderLoading } =
    useTrackedOrderSubmit(amount);

  const [
    depositContinue3DS,
    { error: errorContinue3DS, loading: loadingContinue3DS },
  ] = useMutation(continueDeposit3DS);

  const [
    depositComplete3DS,
    { error: errorComplete3DS, loading: loadingComplete3DS },
  ] = useMutation(completeDeposit3DS);

  const continueOrComplete3DSError = errorContinue3DS || errorComplete3DS;

  const handleChallengeSuccess = async () => {
    try {
      const result = await depositContinue3DS({
        variables: { token: externalTransactionToken, basketID },
      });

      // @ts-expect-error CardPaymentDeclined would have an error object
      if (result?.data?.continue3DS.error) {
        setChallengeState(SPREEDLY_CHALLENGE_STATES.PROCESSING_ERROR);
      } else if (result?.data?.continue3DS) {
        submitAndCompleteOrder({
          depositAmount: formatCurrency({
            value: amount,
            currency: CURRENCY_CODE,
          }),
        });
      }
    } catch (_err) {
      setChallengeState(SPREEDLY_CHALLENGE_STATES.NEUTRAL);
    }
  };

  const handleTriggerCompletion = async (event: ThreeDSEvent) => {
    try {
      // 1. make a request to your backend to do an authenticated call to Spreedly to complete the request
      const result = await depositComplete3DS({
        variables: { token: externalTransactionToken, basketID },
      });
      const challenge = result?.data?.complete3DS;

      // 2a. if the transaction is marked as "succeeded" finish your checkout and redirect to success page
      if (challenge?.__typename === 'CardPaymentAuthorised') {
        await handleChallengeSuccess();
        return;
      }
      // 2b. if the transaction is marked "pending" you'll need to call finalize `event.finalize(transaction)` with the transaction data from the authenticated completion call.
      if (challenge?.__typename === 'CardPayment3DSChallenge') {
        setChallengeState(SPREEDLY_CHALLENGE_STATES.NEUTRAL);
        const transaction = JSON.parse(challenge.transactionData);
        event.finalize(transaction);
        return;
      }
      // All other responses treated as error,
      // could be CardPaymentDeclined or unknown error in processing
      setChallengeState(SPREEDLY_CHALLENGE_STATES.PROCESSING_ERROR);
    } catch (_err) {
      setChallengeState(SPREEDLY_CHALLENGE_STATES.PROCESSING_ERROR);
    }
  };

  const on3DSstatusUpdate = async (event: ThreeDSEvent) => {
    setChallengeState(SPREEDLY_CHALLENGE_STATES.LOADING);
    switch (event.action) {
      case 'succeeded':
        await handleChallengeSuccess();
        break;
      case 'error':
        // Whilst the device-fingerprint is completing there is sometimes an error action state,
        // so we can check the context to ignore this
        // TODO: Check with payments team why this is happening - didn't see before 12/01/2023
        if (
          !(
            event.context.state === 'pending' &&
            event.context.step === 'device-fingerprint'
          )
        ) {
          setChallengeState(SPREEDLY_CHALLENGE_STATES.PROCESSING_ERROR);
        }
        break;
      case 'finalization-timeout':
        setChallengeState(SPREEDLY_CHALLENGE_STATES.TIMEOUT_ERROR);
        break;
      case 'trigger-completion': {
        await handleTriggerCompletion(event);
        break;
      }
      case 'challenge':
        setChallengeState(SPREEDLY_CHALLENGE_STATES.NEUTRAL);
        break;
      case 'device-fingerprint':
        // Deliberately ignored, as this is handled by Spreedly.
        break;
      default:
        setChallengeState(SPREEDLY_CHALLENGE_STATES.LOADING);
        break;
    }
  };

  useEffect(() => {
    const startThreeDSCheck = () => {
      if (challengeStarted) {
        return;
      }

      const lifecycle = new window.Spreedly.ThreeDS.Lifecycle({
        challengeIframeClasses: challenge_container_class,
        challengeIframeLocation: challenge_frame_id,
        environmentKey: SPREEDLY_ENVIRONMENT_KEY,
        hiddenIframeLocation: hidden_frame_id,
        transactionToken: externalTransactionToken,
      });

      window.Spreedly.on('3ds:status', on3DSstatusUpdate);
      lifecycle.start(transactionData);

      setChallengeStarted(true);
    };

    startThreeDSCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); //We only want this effect to run once when the component mounts, so don't want it to run on dependency changes

  useEffect(() => {
    if (loadingContinue3DS || loadingComplete3DS) {
      setChallengeState(SPREEDLY_CHALLENGE_STATES.LOADING);
    }
  }, [loadingContinue3DS, loadingComplete3DS]);

  if (
    continueOrComplete3DSError ||
    challengeState === SPREEDLY_CHALLENGE_STATES.PROCESSING_ERROR
  ) {
    return <DepositErrorComponent />;
  }

  if (
    challengeState === SPREEDLY_CHALLENGE_STATES.TIMEOUT_ERROR ||
    submitOrderError
  ) {
    return (
      <DepositErrorComponent
        message={
          submitOrderError?.message || 'Sorry, your session has timed out.'
        }
      />
    );
  }

  return (
    <Box className={challenge_container_class}>
      {(challengeState === SPREEDLY_CHALLENGE_STATES.LOADING ||
        submitOrderLoading) && <Spinner size="md" />}
      <Box>
        <Box id={hidden_frame_id} />
        <Box id={challenge_frame_id} />
      </Box>
    </Box>
  );
};

export { ProcessPayment };
